import { State, Selector, Action, StateContext } from '@ngxs/store';
import {
    AddItemToCartAction,
    AddItemToCartSuccessAction,
    AddItemToCartFailAction,
    RemoveItemFromCartAction,
    RemoveItemFromCartSuccessAction,
    RemoveItemFromCartFailAction,
    ClearItemsFromCartAction,
    ClearItemsFromCartSuccessAction,
    ClearItemsFromCartFailAction,
    LoadCartItemsAction,
    LoadCartItemsSuccessAction,
    LoadCartItemsFailAction,
    ClearCartStateAction,
    ValidateCartPricesAndPlaceOrderAction,
    // UpdateTotalPriceAction
} from './cart.actions';
import { catchError, tap } from 'rxjs/operators';
import { CartClient, CartModel, ClearCartCommand } from '@core/services/api.service';
import { Injectable } from '@angular/core';
import { OpenToastrAction } from '@ngxs-store/ui-store/ui.actions';
import { ToastrService } from 'ngx-toastr';
import { ToastrType } from '@core/models/ui/toastr-config';
import { CreateOrderAction } from '@ngxs-store/order-store/order.actions';

export class CartStateModel {
    public isLoading: boolean;
    public cartItems: CartModel;
}

@State<CartStateModel>({
    name: 'cart',
    defaults: {
        isLoading: false,
        cartItems: new CartModel()
    }
})

@Injectable()
export class CartState {
    constructor(
        private cartService: CartClient, private toaster: ToastrService
    ) { }

    @Selector()
    static isLoading(state: CartStateModel) {
        return state.isLoading;
    }

    @Selector()
    static Cart(state: CartStateModel) {
        return state.cartItems;

    }
    @Selector()
    static getTotalPriceIncl(state: CartStateModel) {
        return state.cartItems.totalPriceIncl;


    }

    @Selector()
    static getTotalPriceExcl(state: CartStateModel) {
        return state.cartItems.totalPriceExcl;


    }
    @Selector()
    static getNoOfItems(state: CartStateModel) {
        return state.cartItems.numberOfItems ?? 0;
    }


    @Action(AddItemToCartAction)
    addItemToCartAction(ctx: StateContext<CartStateModel>, action: AddItemToCartAction) {
        ctx.patchState({
            isLoading: true
        });

        return this.cartService.addItemToCart(action.payload.command).pipe(
            tap((result) => ctx.dispatch(new AddItemToCartSuccessAction({ CartModel: result }))),
            catchError((err) => ctx.dispatch(new AddItemToCartFailAction(err))),
        );
    }

    @Action(AddItemToCartSuccessAction)
    addItemToCartSuccessAction(ctx: StateContext<CartStateModel>, action: AddItemToCartSuccessAction) {
        ctx.patchState({
            isLoading: false,
            cartItems: action.payload.CartModel,
        });
        ctx.dispatch(new OpenToastrAction({ message: 'Item(s) added to cart', type: ToastrType.success, title: 'Success' }));
    }

    @Action(AddItemToCartFailAction)
    addItemToCartFailAction(ctx: StateContext<CartStateModel>, action: AddItemToCartFailAction) {
        ctx.patchState({
            isLoading: false,
            cartItems: null,
        });
    }

    @Action(RemoveItemFromCartAction)
    removeItemFromCartAction(ctx: StateContext<CartStateModel>, action: RemoveItemFromCartAction) {
        ctx.patchState({
            isLoading: true
        });

        return this.cartService.removeItemFromCart(action.payload.command).pipe(
            tap((result) => ctx.dispatch(new RemoveItemFromCartSuccessAction({ CartModel: result }))),
            catchError((err) => ctx.dispatch(new RemoveItemFromCartFailAction(err)))
        );
    }

    @Action(RemoveItemFromCartSuccessAction)
    removeItemFromCartSuccessAction(ctx: StateContext<CartStateModel>, action: RemoveItemFromCartSuccessAction) {
        ctx.patchState({
            isLoading: false,
            cartItems: action.payload.CartModel,
        });
    }

    @Action(RemoveItemFromCartAction)
    removeItemFromCartFailAction(ctx: StateContext<CartStateModel>, action: RemoveItemFromCartFailAction) {
        ctx.patchState({
            isLoading: true,
            //cartItems: null,
        });
    }

    @Action(ClearItemsFromCartAction)
    clearItemsFromCartAction(ctx: StateContext<CartStateModel>, action: ClearItemsFromCartAction) {
        ctx.patchState({
            isLoading: true
        });

        return this.cartService.clearUserCart(new ClearCartCommand()).pipe(
            tap((result) => ctx.dispatch(new ClearItemsFromCartSuccessAction({ result }))),
            catchError((err) => ctx.dispatch(new ClearItemsFromCartFailAction(err)))
        );
    }

    @Action(ClearItemsFromCartSuccessAction)
    clearCartItemsSuccessAction(ctx: StateContext<CartStateModel>, action: ClearItemsFromCartSuccessAction) {
        ctx.patchState({
            isLoading: false
        });
        ctx.dispatch(new LoadCartItemsAction());
    }

    @Action(ClearItemsFromCartFailAction)
    clearItemsFromCartFailAction(ctx: StateContext<CartStateModel>, action: ClearItemsFromCartFailAction) {
        ctx.patchState({
            isLoading: false,
            cartItems: null,
        });
    }

    @Action(LoadCartItemsAction)
    LoadCartItemsAction(ctx: StateContext<CartStateModel>, action: LoadCartItemsAction) {
        ctx.patchState({
            isLoading: true
        });

        return this.cartService.getUserCart().pipe(
            tap((result) => ctx.dispatch(new LoadCartItemsSuccessAction({ CartModel: result }))),
            catchError((err) => ctx.dispatch(new LoadCartItemsFailAction(err)))
        );
    }


    @Action(LoadCartItemsSuccessAction)
    loadCartItemsSuccessAction(ctx: StateContext<CartStateModel>, action: LoadCartItemsSuccessAction) {
        ctx.patchState({
            isLoading: false,
            cartItems: action.payload.CartModel,
        });

    }


    @Action(LoadCartItemsFailAction)
    loadCartItemsFailAction(ctx: StateContext<CartStateModel>, action: LoadCartItemsFailAction) {
        ctx.patchState({
            isLoading: false,
            cartItems: null,
        });
    }

    @Action(ClearCartStateAction)
    clearCartStateAction(ctx: StateContext<CartStateModel>, action: ClearCartStateAction) {
        ctx.patchState({
            isLoading: false,
            cartItems: null
        });
    }

    @Action(ValidateCartPricesAndPlaceOrderAction)
    validateCartPricesAndPlaceOrderAction(ctx: StateContext<CartStateModel>, action: ValidateCartPricesAndPlaceOrderAction) {
        return this.cartService.getUserCart().pipe(
            tap((result) => {
                if (action.payload.command.order.totalPriceIncl !== parseFloat(result.totalPriceIncl.toFixed(2))) {
                    ctx.dispatch(new OpenToastrAction({ message: 'Cart prices updated. Please review cart and place order again.', type: ToastrType.warning, title: 'Warning' }));
                } else {
                    ctx.dispatch(new CreateOrderAction({ createOrderCommand: action.payload.command }))
                }
            }),
            catchError((err) => ctx.dispatch(new LoadCartItemsFailAction(err)))
        );
    }
}
